import {
  Component,
  Output,
  EventEmitter,
  ElementRef,
  HostListener,
  ViewChild,
  Renderer2,
  OnDestroy,
} from '@angular/core';

interface AddOption {
  label: string;
  action: 'document' | 'folder';
}

@Component({
  selector: 'app-add-document-dropdown',
  templateUrl: './add-document-dropdown.component.html',
  styleUrls: ['./add-document-dropdown.component.scss'],
})
export class AddDocumentDropdownComponent implements OnDestroy {
  @Output() addDocument = new EventEmitter<void>();
  @Output() addFolder = new EventEmitter<void>();
  @ViewChild('popover', { static: false }) popover?: ElementRef<HTMLElement>;
  @ViewChild('trigger', { static: false }) trigger?: ElementRef<HTMLElement>;

  isOpen = false;
  private resizeListener?: () => void;
  private scrollListener?: () => void;

  readonly options: AddOption[] = [
    { label: 'Add Document', action: 'document' },
    { label: 'Add Folder', action: 'folder' },
  ];

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Position dropdown on mobile after it opens
      setTimeout(() => this.positionDropdown(), 0);
      // Listen for resize to reposition
      this.resizeListener = this.renderer.listen('window', 'resize', () => {
        if (this.isOpen) {
          this.positionDropdown();
        }
      });
      // Listen for scroll to reposition
      this.scrollListener = this.renderer.listen('document', 'scroll', () => {
        if (this.isOpen) {
          this.positionDropdown();
        }
      });
    } else {
      if (this.resizeListener) {
        this.resizeListener();
        this.resizeListener = undefined;
      }
      if (this.scrollListener) {
        this.scrollListener();
        this.scrollListener = undefined;
      }
    }
  }

  private positionDropdown(): void {
    if (!this.trigger?.nativeElement || !this.popover?.nativeElement) {
      return;
    }

    const trigger = this.trigger.nativeElement;
    const popover = this.popover.nativeElement;
    const rect = trigger.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // On mobile, position fixed relative to viewport
      const top = rect.bottom + 4; // 4px gap
      const left = 8; // 8px from left edge
      const right = 8; // 8px from right edge
      
      this.renderer.setStyle(popover, 'position', 'fixed');
      this.renderer.setStyle(popover, 'top', `${top}px`);
      this.renderer.setStyle(popover, 'left', `${left}px`);
      this.renderer.setStyle(popover, 'right', `${right}px`);
      this.renderer.setStyle(popover, 'width', 'auto');
    } else {
      // On desktop, use default absolute positioning
      this.renderer.removeStyle(popover, 'position');
      this.renderer.removeStyle(popover, 'top');
      this.renderer.removeStyle(popover, 'left');
      this.renderer.removeStyle(popover, 'right');
      this.renderer.removeStyle(popover, 'width');
    }
  }

  select(option: AddOption): void {
    if (option.action === 'document') {
      this.addDocument.emit();
    } else if (option.action === 'folder') {
      this.addFolder.emit();
    }
    this.isOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  ngOnDestroy(): void {
    if (this.resizeListener) {
      this.resizeListener();
    }
    if (this.scrollListener) {
      this.scrollListener();
    }
  }
}
